// مكون حوار إرجاع الصيانة
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Typography,
  Box,
  Alert,
  Divider,
  Grid
} from '@mui/material';
// import { LoadingButton } from '@mui/lab';
import { 
  MaintenanceReturnReason, 
  MaintenanceRecord 
} from '../../utils/types';
import { returnReasonLabels } from '../../utils/constants';
import { createMaintenanceReturn } from '../../services/maintenanceReturnService';
import { useAuth } from '../../contexts/AuthContext';

interface MaintenanceReturnDialogProps {
  open: boolean;
  onClose: () => void;
  maintenance: MaintenanceRecord | null;
  onReturnCreated: () => void;
}

const MaintenanceReturnDialog: React.FC<MaintenanceReturnDialogProps> = ({
  open,
  onClose,
  maintenance,
  onReturnCreated
}) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // بيانات النموذج
  const [formData, setFormData] = useState({
    returnReason: MaintenanceReturnReason.SAME_ISSUE,
    customReturnReason: '',
    issueDescription: '',
    refundAmount: 0,
    refundMethod: 'cash' as 'cash' | 'debt_reduction',
    deviceReturned: true,
    notes: ''
  });

  // إعادة تعيين النموذج عند فتح الحوار
  React.useEffect(() => {
    if (open && maintenance) {
      setFormData({
        returnReason: MaintenanceReturnReason.SAME_ISSUE,
        customReturnReason: '',
        issueDescription: maintenance.issue || '',
        refundAmount: maintenance.totalPaid || 0,
        refundMethod: 'cash',
        deviceReturned: true,
        notes: ''
      });
      setError(null);
    }
  }, [open, maintenance]);

  // معالجة تغيير القيم
  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // إذا تم تغيير سبب الإرجاع، قم بتحديث وصف العيب
    if (field === 'returnReason') {
      if (value === MaintenanceReturnReason.SAME_ISSUE) {
        setFormData(prev => ({
          ...prev,
          issueDescription: maintenance?.issue || ''
        }));
      } else if (value === MaintenanceReturnReason.NEW_ISSUE) {
        setFormData(prev => ({
          ...prev,
          issueDescription: ''
        }));
      }
    }
  };

  // معالجة الإرسال
  const handleSubmit = async () => {
    if (!maintenance || !currentUser) return;

    // التحقق من صحة البيانات
    if (!formData.issueDescription.trim()) {
      setError('يرجى إدخال وصف العيب');
      return;
    }

    if (formData.returnReason === MaintenanceReturnReason.OTHER && !formData.customReturnReason.trim()) {
      setError('يرجى إدخال سبب الإرجاع المخصص');
      return;
    }

    if (formData.refundAmount < 0) {
      setError('مبلغ الاسترداد لا يمكن أن يكون سالباً');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await createMaintenanceReturn({
        maintenanceId: maintenance.id,
        returnReason: formData.returnReason,
        customReturnReason: formData.customReturnReason || undefined,
        issueDescription: formData.issueDescription,
        refundAmount: formData.refundAmount,
        refundMethod: formData.refundMethod,
        deviceReturned: formData.deviceReturned,
        notes: formData.notes || undefined,
        employeeId: (currentUser as any).uid || currentUser.email || 'unknown',
        employeeName: (currentUser as any).displayName || currentUser.email || 'غير معروف'
      });

      onReturnCreated();
      onClose();
    } catch (error: any) {
      console.error('خطأ في إنشاء الإرجاع:', error);
      setError(error.message || 'حدث خطأ أثناء إنشاء الإرجاع');
    } finally {
      setLoading(false);
    }
  };

  if (!maintenance) return null;

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: { minHeight: '600px' }
      }}
    >
      <DialogTitle>
        <Typography variant="h6" component="div">
          إرجاع صيانة - {maintenance.customerName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {maintenance.deviceType} - {(maintenance as any).brand} {(maintenance as any).model}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* سبب الإرجاع */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>سبب الإرجاع</InputLabel>
              <Select
                value={formData.returnReason}
                label="سبب الإرجاع"
                onChange={(e) => handleChange('returnReason', e.target.value)}
              >
                {Object.entries(returnReasonLabels).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* سبب مخصص إذا كان السبب "أخرى" */}
          {formData.returnReason === MaintenanceReturnReason.OTHER && (
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="سبب الإرجاع المخصص"
                value={formData.customReturnReason}
                onChange={(e) => handleChange('customReturnReason', e.target.value)}
                required
              />
            </Grid>
          )}

          {/* وصف العيب */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="وصف العيب"
              multiline
              rows={3}
              value={formData.issueDescription}
              onChange={(e) => handleChange('issueDescription', e.target.value)}
              required
              helperText={
                formData.returnReason === MaintenanceReturnReason.SAME_ISSUE 
                  ? "نفس العيب الأصلي" 
                  : formData.returnReason === MaintenanceReturnReason.NEW_ISSUE
                  ? "وصف العيب الجديد"
                  : "وصف تفصيلي للمشكلة"
              }
            />
          </Grid>

          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="h6" gutterBottom>
              تفاصيل الاسترداد المالي
            </Typography>
          </Grid>

          {/* مبلغ الاسترداد */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="مبلغ الاسترداد (ج.م)"
              type="number"
              value={formData.refundAmount}
              onChange={(e) => handleChange('refundAmount', parseFloat(e.target.value) || 0)}
              inputProps={{ min: 0, step: 0.01 }}
              helperText={`المبلغ المدفوع: ${maintenance.totalPaid || 0} ج.م`}
            />
          </Grid>

          {/* طريقة الاسترداد */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>طريقة الاسترداد</InputLabel>
              <Select
                value={formData.refundMethod}
                label="طريقة الاسترداد"
                onChange={(e) => handleChange('refundMethod', e.target.value)}
              >
                <MenuItem value="cash">استرداد نقدي</MenuItem>
                <MenuItem value="debt_reduction">تقليل الدين</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* هل تم إرجاع الجهاز */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.deviceReturned}
                  onChange={(e) => handleChange('deviceReturned', e.target.checked)}
                />
              }
              label="تم إرجاع الجهاز للعميل"
            />
          </Grid>

          {/* ملاحظات */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="ملاحظات إضافية"
              multiline
              rows={2}
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
              placeholder="أي ملاحظات إضافية حول الإرجاع..."
            />
          </Grid>
        </Grid>

        {/* تحذير الاسترداد النقدي */}
        {formData.refundMethod === 'cash' && formData.refundAmount > 0 && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>تنبيه:</strong> سيتم خصم مبلغ {formData.refundAmount} ج.م من خزينة اليوم.
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          إلغاء
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={loading}
          variant="contained"
          color="warning"
        >
          {loading ? 'جاري المعالجة...' : 'تأكيد الإرجاع'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MaintenanceReturnDialog;
